import React, { useMemo } from 'react';
import Modal from '@omilia/modal';
import { Form, Field } from 'react-final-form';
import Button, { ButtonGroup } from '@omilia/button';

import TextFieldFinalFormWrapper from 'components/TextFieldFinalFormWrapper';
import { SSelect } from 'components/styled';
import validate from './utils/validate';
import {
  SForm,
  SInputsContainer,
  SFormGroupSelect,
  SField,
  SGroupHeading,
} from './styled';

type Order = { index: number; order: string };

export type Props = {
  count: number;
  modelName: string;
  orderList: Order[];
  isGrouped: boolean;
  groupOrder: number;
  onClose: () => void;
  onSubmit: ({
    order,
    ruleGroupName,
  }: {
    order: number | string;
    ruleGroupName: string;
  }) => void;
};

const CreateRuleGroupOrRuleModal = ({
  groupOrder,
  isGrouped = false,
  count,
  modelName,
  orderList,
  onClose,
  onSubmit,
}: Props) => {
  const initialOrderValue = useMemo(
    () =>
      isGrouped
        ? {
            label: `${groupOrder}.${count + 1}`,
            value: `${groupOrder}.${count + 1}`,
          }
        : { label: `${count + 1}`, value: count + 1 },
    [count, isGrouped, groupOrder],
  );

  const selectItems = useMemo(
    () => [
      {
        label: 'firstAndLastGroup',
        options: [
          {
            label: 'First',
            value: isGrouped
              ? orderList[0]?.index ?? 1
              : (orderList[0]?.index ?? 0) + 1,
          },
          {
            label: 'Last',
            value: isGrouped
              ? orderList[orderList?.length - 1]?.index ?? 1
              : (orderList[orderList?.length - 1]?.index ?? 0) + 1,
          },
        ],
      },
      {
        label: 'optionsGroup',
        options: [
          ...orderList.map(({ index, order }: Order) => ({
            label: order,
            value: isGrouped ? index : index + 1,
          })),
          initialOrderValue,
        ],
      },
    ],
    [orderList, initialOrderValue, isGrouped],
  );

  const GroupHeading = ({ children }: { children: string }) =>
    children === 'optionsGroup' ? <SGroupHeading /> : null;

  return (
    <Modal
      open
      shouldCloseOnBackdropClick
      shouldCloseOnEscPress
      glyph="rule-group"
      title={`Create Rule ${isGrouped ? '' : 'Group'}`}
      onClose={onClose}
    >
      <Form
        keepDirtyOnReinitialize
        initialValues={{
          order: initialOrderValue,
          ruleGroupName: '',
        }}
        onSubmit={({ order: { value }, ruleGroupName }) =>
          onSubmit({ order: value, ruleGroupName })
        }
      >
        {({ handleSubmit, invalid, submitting }) => (
          <SForm onSubmit={handleSubmit}>
            <SInputsContainer data-test="rule-order">
              <SFormGroupSelect>
                <Field
                  label="Order"
                  name="order"
                  placeholder="Order"
                  render={props => (
                    <SSelect
                      components={{
                        GroupHeading,
                      }}
                      options={selectItems}
                      {...props}
                      {...props.input}
                    />
                  )}
                />
              </SFormGroupSelect>
              <SField>
                <Field
                  component={TextFieldFinalFormWrapper}
                  data-test="rule-group-name"
                  label={`${isGrouped ? 'Rule' : 'Group'} Name`}
                  name="ruleGroupName"
                  placeholder={`Type ${isGrouped ? 'Rule' : 'Group'} Name`}
                  validate={value =>
                    validate(value, { count, modelName, isGrouped: false })
                  }
                />
              </SField>
            </SInputsContainer>
            <ButtonGroup>
              <Button
                color="secondary"
                data-test="cancel-button"
                variant="text"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                data-test="create-button"
                disabled={invalid}
                loading={submitting}
                type="submit"
                variant="contained"
              >
                Create
              </Button>
            </ButtonGroup>
          </SForm>
        )}
      </Form>
    </Modal>
  );
};

export default CreateRuleGroupOrRuleModal;
